import React from 'react'
import { Link } from 'react-router-dom'
import { SimpleTooltip, SimpleTooltipProvider } from '@/components/ui-primitives/simple-tooltip'
import { mergeClasses } from '@/helpers/styling'
import { hasTextChild } from '@/utils/has-text-child'

export function TopBar(props: {
  children: React.ReactNode
}) {
  return (
    <div className="
          flex items-center justify-between
          py-[4px] px-[4px] gap-1
          text-xs text-white font-exo
          bg-wistor
          select-none
          "
         style={{ zIndex: 2000 }}>
      <SimpleTooltipProvider>
        {props.children}
      </SimpleTooltipProvider>
    </div>
  )
}

export function TopBarButton(props: {
  variant?: "ghost" | "outline" | "primary" | "purple"
  to?: string
  href?: string
  onClick?: () => void
  title?: string
  active?: boolean
  disabled?: boolean
  className?: string
  children: React.ReactNode
}) {
  const variant = props.variant ?? "ghost"
  const isLabeled = hasTextChild(props.children)
  const classNames = mergeClasses(`
    flex flex-shrink items-center justify-center gap-2
    h-[30px] min-w-[30px] p-1 rounded-md
    font-medium text-[13px]
    overflow-hidden text-ellipsis whitespace-nowrap
    text-gray-200 hover:text-gray-200
  `, {
    "!px-[10px]": isLabeled,
    "border !border-gray-400 !border-opacity-40": variant === "outline",
    "mx-[1px]": variant === "ghost", // keep position when switching between ghost and other
    "bg-primary text-white": variant === "primary",
    "bg-[#51479e] text-white": variant === "purple",
    "opacity-50 cursor-default": props.disabled,
    [`cursor-pointer
      hover:bg-[#2f2a72] hover:text-gray-300
      active:bg-[#37327c]
      focus:bg-[#4c487e]`]: !props.disabled,
    "focus:!outline focus:!outline-1 focus:!outline-wistor-alt": !props.disabled && variant !== "ghost"
  }, props.className)

  const button = props.to ? (
    <Link to={props.to} className={mergeClasses("hover:no-underline", classNames)}>
      {props.children}
    </Link>
  ) : props.href ? (
    <a href={props.href}
       target="_blank"
       rel="noopener noreferrer"
       className={mergeClasses("hover:no-underline", classNames)}>
      {props.children}
    </a>
  ) : (
    <button type="button"
            onClick={props.onClick}
            disabled={props.disabled}
            className={classNames}>
      {props.children}
    </button>
  )

  return (
    <SimpleTooltip tooltip={props.title}
                   delayDuration={700}
                   tooltipProps={{ side: "bottom", sideOffset: 2 }}>
      {button}
    </SimpleTooltip>
  )
}

export function TopBarSeparator() {
  return (
    <div className="min-h-full mx-0.5 my-[1px] border-l border-dotted !border-gray-400 !border-opacity-40" />
  )
}
